import * as React from 'react';
import { Form, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import SessionForm from '../../../../components/forms/SessionForm';
import { H1 } from '../../../../components/ui/Typography';
import AntdCustomButton from '../../../../components/buttons/AntdCustomButton';
import CategorySearch from '../CategorySearch';
import StepperModel from '../../../../models/StepperModel';
import { STEP_CATEGORY_KEYWORDS } from '../../../steps-definitions';
import BusinessModel from '../../models/BusinessModel';
import ScreenNavigationModel, {
  ScreenType
} from '../../models/ScreenNavigationModel';
import { WIZARD_STEPS } from '../../../wizard-steps-description';
import type Step from '../../Step';
import HelpBoxCompanionModel from '../../../../models/HelpBoxCompanionModel';
import { motion, AnimatePresence } from 'framer-motion';
import {
  fadeInUpVariants,
  staggerContainerVariants
} from '../../../../components/animations/StepAnimations';

const ChooseCorporateCategory: React.FC = observer(() => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [isSaveLoading, setIsSaveLoading] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(true);
  const [nextScreen, setNextScreen] = React.useState<ScreenType | null>(null);

  const helpBoxTitleMessage = t('What is a corporate category?');
  const helpBoxParagraphMessage = t('It corresponds to your industry and is based on the categories that Google uses to classify companies.');

  React.useEffect(() => {
    HelpBoxCompanionModel.setTitleMessage(helpBoxTitleMessage);
    HelpBoxCompanionModel.setParagraphMessage(helpBoxParagraphMessage);
  }, [i18n.language]);

  const handleScreenChange = () => {
    setIsVisible(false);
  };

  const handleSave = React.useCallback(async (values: any) => {
    setIsSaveLoading(true);
    try {
      if (values.category) {
        BusinessModel.selectedCategoryName = values.category;
      }

      const selectedBusiness = BusinessModel.getSelectedBusiness();
      const hasWebsite = selectedBusiness?.website && selectedBusiness.website.length > 0;

      if (!hasWebsite) {
        setNextScreen(ScreenType.ENTER_COMPANY_DOMAIN);
        handleScreenChange();
        return;
      }

      await (
        WIZARD_STEPS[StepperModel.currentStep].step as Step
      ).moveToNextStep();
    } catch (e) {
      message.error(t('An error occurred. Please try again.'));
      console.error(e);
    } finally {
      setIsSaveLoading(false);
    }
  }, []);

  return (
    <AnimatePresence
      onExitComplete={() => {
        if (nextScreen) {
          setTimeout(() => {
            ScreenNavigationModel.setCurrentScreen(nextScreen);
          }, 200);
        }
      }}>
      {isVisible && (
        <motion.div
          className="flex items-start sm:items-center sm:text-center justify-start sm:justify-center flex-1"
          initial="hidden"
          animate="visible"
          variants={staggerContainerVariants}
          exit="hidden">
          <div className="w-full max-w-[800px] min-h-[1vh] company-data-form">
            <motion.div variants={fadeInUpVariants}>
              <H1>{t('Choose a corporate category')}</H1>
            </motion.div>

            <SessionForm
              form={form}
              formName="chooseCorporateCategoryForm"
              formProps={{
                layout: 'vertical',
                onFinish: handleSave
              }}>
              <motion.div variants={fadeInUpVariants}>
                <Form.Item
                  label={
                    <span className="font-semibold text-base">
                      {t('Select your category')}
                    </span>
                  }
                  name="category"
                  rules={[{ required: true, message: t('Please select a category') }]}
                  className="mb-6">
                  <CategorySearch
                    onChange={(value) => {
                      form.setFieldsValue({ category: value });
                    }}
                  />
                </Form.Item>
              </motion.div>

              <motion.div variants={fadeInUpVariants}>
                <Form.Item 
                  className="flex justify-center mb-0"
                  shouldUpdate>
                  {({ getFieldValue }) => {
                    const selectedCategory = getFieldValue('category');
                    
                    return (
                      <AntdCustomButton
                        appearance="important"
                        htmlType="submit"
                        disabled={!selectedCategory}
                        loading={isSaveLoading}
                      >
                        {t('Save')}
                      </AntdCustomButton>
                    );
                  }}
                </Form.Item>
              </motion.div>
            </SessionForm>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default ChooseCorporateCategory;